import React, { forwardRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import { InputWrapper } from '../InputWrapper/InputWrapper';
import PropTypes from 'prop-types';
import hljs from 'highlight.js';
import 'react-quill/dist/quill.snow.css';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github.css'; // You can choose different styles

// Import KaTeX globally
if (typeof window !== 'undefined') {
  window.katex = require('katex');
  window.hljs = hljs;
}

// Configure highlight.js
hljs.configure({
  languages: [
    'javascript',
    'python',
    'java',
    'cpp',
    'ruby',
    'php',
    'sql',
    'bash',
    'html',
    'css',
    'json',
    'yaml',
    'markdown',
    'typescript'
  ]
});

const defaultModules = {
  syntax: {
    highlight: text => hljs.highlightAuto(text).value
  },
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['formula'],
      ['clean']
    ]
  },
  clipboard: {
    matchVisual: false
  },
  history: {
    delay: 2000,
    maxStack: 500,
    userOnly: true
  }
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'script',
  'color', 'background',
  'align', 'direction',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
  'formula', 'code-block',
  'blockquote',
  'clean'
];

export const RichTextEditor = forwardRef(({ 
  label,
  error, 
  helperText,
  layout,
  labelCol,
  wrapperCol,
  required,
  value,
  onChange,
  height = 200,
  customModules = {},
  placeholder = 'Write something...',
  ...props 
}, ref) => {
  // Merge custom modules with default modules
  const modules = {
    ...defaultModules,
    ...customModules
  };

  return (
    <InputWrapper
      label={label}
      error={error}
      helperText={helperText}
      required={required}
      layout={layout}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
    >
      <ReactQuill
        ref={ref}
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        theme="snow"
        style={{ 
          height: height,
          marginBottom: '2.5rem' 
        }}
        {...props}
      />
    </InputWrapper>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

RichTextEditor.propTypes = {
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  labelCol: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number,
  }),
  wrapperCol: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number,
  }),
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  height: PropTypes.number,
  customModules: PropTypes.object,
};

export default RichTextEditor; 